import { useEffect } from 'react'
import { SuggestionRequestStatus } from 'types'

type Props = {
  status: SuggestionRequestStatus | undefined
  setShowError: (show: boolean) => void
  percentComplete: number
  setPercentComplete: React.Dispatch<React.SetStateAction<number>>
  generatedContent?: string | null
  totalDuration?: number // Total duration in milliseconds
}

export const useFakeTimer = ({
  status,
  generatedContent,
  setShowError,
  percentComplete,
  setPercentComplete,
  totalDuration = 55000, // Default to 55 seconds
}: Props) => {
  useEffect(() => {
    let intervalId: NodeJS.Timeout
    let quickFinishInterval: NodeJS.Timeout
    let errorTimeout: NodeJS.Timeout | undefined = undefined
    const startTime = Date.now()
    const updateInterval = 100

    const updateProgress = () => {
      const currentTime = Date.now()
      const elapsedTime = currentTime - startTime
      let progress = Math.min((elapsedTime / totalDuration) * 100, 99) // Cap at 99% until complete
      if (progress === 99) {
        errorTimeout = setTimeout(() => {
          if (!generatedContent && status !== 'successful') {
            setShowError(true)
          }
        }, 10000)
        return
      }

      // If content is generated, accelerate to 100%
      if (generatedContent) {
        // Clear error timeout since we have contentAdd commentMore actions
        clearTimeout(errorTimeout)

        const remainingProgress = 100 - percentComplete
        progress = Math.min(percentComplete + remainingProgress * 0.1, 100)

        if (progress >= 100) {
          clearInterval(intervalId)
        }
      }

      setPercentComplete(Math.round(progress))
    }

    // Start the progress animation
    if (status === 'started' || status === 'processing') {
      intervalId = setInterval(updateProgress, updateInterval)
    }

    // When status changes to complete, quickly finish the progress
    if (status === 'successful') {
      clearTimeout(errorTimeout)

      quickFinishInterval = setInterval(() => {
        setPercentComplete((prev) => {
          const newProgress = prev + 2
          if (newProgress >= 100) {
            clearInterval(quickFinishInterval)
            return 100
          }
          return newProgress
        })
      }, 50)
    }

    return () => {
      clearInterval(intervalId)
      clearInterval(quickFinishInterval)
      clearTimeout(errorTimeout)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, generatedContent])
}
